import React from 'react';
import Input from 'components/Inputs/Input/Input';
import { getLabTypes } from 'api/api';
import { useQuery } from '@tanstack/react-query';
import CheckBox from 'components/Inputs/CheckBox/CheckBox';
import { LabType } from '../../../types/types';
import Modal from '../Modal';
import Icon from '../../Icon/Icon';
import Button from '../../Button/Button';

type ModalProps = {
  onCloseModal: () => void;
  onAddLabTests: (lab: { labTestTypes: LabType[]; isAtHomeTest: boolean }) => void;
  addedLabTests: LabType[];
  isRequestingLab: boolean;
};

const labsTypesQuery = () => ({
  queryKey: ['lab-types'],
  queryFn: () => getLabTypes(),
});

function LabTests({ onCloseModal, onAddLabTests, addedLabTests, isRequestingLab }: ModalProps) {
  const [filteredLabTypes, setFilteredLabTypes] = React.useState<LabType[] | undefined>(undefined);
  const [selectedLabTypes, setSelectedLabTypes] = React.useState<LabType[]>(addedLabTests);
  const [confirmLabTests, setConfirmLabTests] = React.useState(false);

  const showFullLabListView =
    selectedLabTypes.length > 0 && addedLabTests.length !== selectedLabTypes.length;

  const { data, isLoading } = useQuery<LabType[]>({
    ...labsTypesQuery(),
  });

  const fetchedLabTypes = (data || []) satisfies LabType[];

  const handleLabSearch = (value: string) => {
    const filter = fetchedLabTypes.filter(test =>
      test.name.toLowerCase().includes(value.toLowerCase()),
    );

    setFilteredLabTypes(!value ? undefined : filter);
  };

  const handleCheckInputChange = (labTest: LabType) => {
    setSelectedLabTypes(fndLabTypes => {
      const tests = fndLabTypes.filter(fndItem => fndItem.id !== labTest.id);
      if (tests.length < fndLabTypes.length) {
        return tests;
      }
      return [...fndLabTypes, labTest];
    });
  };

  const handleSubmitLabTest = () => {
    if (selectedLabTypes.length > 0 && !confirmLabTests) {
      setConfirmLabTests(true);
      return;
    }

    const payload = {
      labTestTypes: selectedLabTypes.filter(fndLabType => !isAnAlreadyAddedLabTest(fndLabType.id)),
      isAtHomeTest: false,
    };

    onAddLabTests(payload);
  };

  const isAnAlreadyAddedLabTest = (labTestId: string) =>
    Boolean(addedLabTests.find(fndLabTest => fndLabTest.id === labTestId));

  return (
    <Modal onClose={onCloseModal} show>
      <div
        className={`flex flex-col rounded-lg bg-white tablet:w-[550px]  space-y-4 pt-8 pb-8 px-8 ${
          selectedLabTypes.length > 0 && confirmLabTests ? '' : 'tablet:min-h-[600px]'
        }`}
      >
        <div className="flex flex-col flex-1">
          <h3 className="text-lg font-semibold inline-flex whitespace-pre mb-4">Request Lab</h3>

          {!(selectedLabTypes.length > 0 && confirmLabTests) ? (
            <>
              <div className="relative mb-5">
                <Icon
                  id="iconSearch"
                  className="absolute w-[15px] text-[#8B969D] h-[15px] my-auto top-0 bottom-0 left-4"
                />
                <div
                  className="!pl-[30px] w-full rounded-full border-[1.5px] border-gray-300 focus-within:border-river-blue
                  focus-within:ring-river-blue focus-within:ring-[0.3px]"
                >
                  <Input
                    name="search"
                    autoComplete="off"
                    placeholder="Search lab test"
                    type="search"
                    className="rounded-full border-none focus:border-none focus:ring-transparent focus:ring-0"
                    onChange={event => {
                      handleLabSearch(event.target.value);
                    }}
                  />
                </div>
              </div>
              <div className="!-mx-8">
                {isLoading ? (
                  <span className="mx-auto text-center mt-[15px] w-28 flex justify-center items-start flex-col">
                    <Icon id="loader" className="w-10 h-10 text-river-black mx-auto" />
                    <span className="loading">Please wait</span>
                  </span>
                ) : !isLoading && (filteredLabTypes || fetchedLabTypes).length === 0 ? (
                  <span className="mx-auto text-center mt-[15px] w-full block">
                    No results found
                  </span>
                ) : (filteredLabTypes || fetchedLabTypes).length > 0 ? (
                  <ul
                    className={`w-full overflow-y-scroll space-y-2 ${
                      showFullLabListView ? 'max-h-[350px]' : 'max-h-[450px]'
                    }`}
                  >
                    {(filteredLabTypes || fetchedLabTypes).map(testType => (
                      <li
                        key={testType.id}
                        role="presentation"
                        onClick={() =>
                          isAnAlreadyAddedLabTest(testType.id)
                            ? false
                            : handleCheckInputChange(testType)
                        }
                        className={`flex group flex-row justify-between items-center text-river-black cursor-pointer hover:bg-[#F3F3F3] py-2 px-8 ${
                          selectedLabTypes.find(fndLabTest => fndLabTest.id === testType.id)
                            ? 'bg-[#F3F3F3]'
                            : ''
                        }`}
                      >
                        <label
                          htmlFor="wantsHomeTesting"
                          className="block pl-[15px] -indent-[15px] cursor-pointer text-[15px]"
                        >
                          <CheckBox
                            id={testType.id}
                            name={testType.id}
                            value={testType.id}
                            checked={Boolean(
                              selectedLabTypes.find(fndLabTest => fndLabTest.id === testType.id),
                            )}
                            onChange={() => {}}
                            className="align-middle cursor-pointer mr-3"
                            disabled={isAnAlreadyAddedLabTest(testType.id)}
                          />
                          <span
                            className={`align-middle ${
                              selectedLabTypes.find(fndLabTest => fndLabTest.id === testType.id)
                                ? 'font-medium'
                                : ''
                            }`}
                          >
                            {testType.name}
                          </span>
                        </label>

                        <span
                          className={`align-middle text-xs ${
                            selectedLabTypes.find(fndLabTest => fndLabTest.id === testType.id)
                              ? 'font-semibold'
                              : ''
                          }`}
                        >
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          }).format(testType.price / 100)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="px-[15px] pt-[15px] pb-[22px] text-center text-river-black block w-full invisible">
                    Error loading Lab Tests
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-river-black text-center mb-5">
                Review the labs information below to make sure it is correct before submitting a new
                request.
              </p>

              <div className="flex flex-col mb-4 tablet:w-[80%] laptop:w-[85%] mx-auto">
                <div className="bg-white rounded-xl shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)]">
                  <div className="bg-river-blue text-white px-6 rounded-t-xl py-3">
                    <h3>Lab Tests Selected</h3>
                  </div>
                  <div>
                    <ul className="divide-y-[0.5px] divide-[#D7D7D7] px-2">
                      {selectedLabTypes.map(lab => (
                        <li className="text-river-black cursor-pointer" key={lab.id}>
                          <div className="py-3 px-6 flex flex-row items-center -mx-[10px] hover:bg-[#F3F3F3] ">
                            <p
                              className={`text-[15px] ${
                                isAnAlreadyAddedLabTest(lab.id) ? '!text-[#9DA4A8]' : 'font-medium'
                              }`}
                            >
                              {lab.name}
                            </p>
                            <button
                              type="button"
                              disabled={isAnAlreadyAddedLabTest(lab.id)}
                              className={`${
                                isAnAlreadyAddedLabTest(lab.id)
                                  ? 'disabled:bg-gray-300 disabled:border-gray-300 '
                                  : 'bg-river-blue'
                              } w-5 h-5 rounded-full flex items-center justify-center ml-auto shrink-0`}
                            >
                              <Icon id="checkmark" className="text-white w-2 h-2" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {showFullLabListView && (
          <div
            className="!mt-8 flex flex-row items-center justify-center 
            tablet:justify-end w-full"
          >
            <Button
              label={!confirmLabTests ? 'Confirm selection' : 'Submit Request'}
              onClick={handleSubmitLabTest}
              className="w-[220px] !rounded-full !py-[10px]"
              disabled={isRequestingLab}
              loading={isRequestingLab}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default LabTests;
